// src/auth/AuthContext.jsx
import React, { createContext, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE || "https://boss.kfdtech.cn/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const nav = useNavigate();

  const login = async (email, password) => {
    const resp = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok || !json?.access_token) {
      throw new Error(json?.detail || json?.error || "Login failed");
    }
    localStorage.setItem("token", json.access_token);
    setToken(json.access_token);
    return true;
  };

  const logout = () => {
    try { localStorage.removeItem("token"); } catch {}
    setToken("");
  };

  // ✅ 关键：统一请求入口
  const authFetch = async (url, opts = {}) => {
    const headers = new Headers(opts.headers || {});
    if (token) headers.set("Authorization", `Bearer ${token}`);

    // 自动补 API_BASE
    const fullUrl = /^https?:\/\//.test(url) ? url : `${API_BASE}${url}`;

    const resp = await fetch(fullUrl, { ...opts, headers });

    // 如果 token 失效，自动清除并跳转登录
    if (resp.status === 401) {
      logout();
      const from = encodeURIComponent(window.location.pathname + window.location.search);
      nav(`/login?from=${from}`, { replace: true });
      throw new Error("Not authenticated");
    }

    return resp;
  };

  // 也顺便给两个便捷方法（可选）
  const getJson = (url) => authFetch(url).then(r => r.json());
  const postJson = (url, body) =>
    authFetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then(r => r.json());

  const value = useMemo(() => ({ token, login, logout, authFetch, getJson, postJson }), [token]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
